import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { setLoading } from 'Actions/loading';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { AlertDialog } from 'Shared/AlertDialog';
import http from 'Utils/http';
import { process } from 'Utils/errorHandler';
import { useCopyText } from 'Utils/clipboard';

const KEY_NAME = /^[a-z0-9_-]{1,64}$/;
const EMPTY_FORM = { name: '', privateKey: '' };

export const SigningKeys = ({ companyId }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const copy = useCopyText();

    const [keys, setKeys] = useState([]);
    const [form, setForm] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const request = useCallback((promise, onSuccess) => {
        dispatch(setLoading(true));
        promise
            .then((res) => onSuccess(res.data))
            .catch((err) => process(err, enqueueSnackbar))
            .finally(() => dispatch(setLoading(false)));
    }, [dispatch, enqueueSnackbar]);

    const load = useCallback(() => {
        request(http.get(`/signing-keys?company_id=${companyId}`), (data) => setKeys(data.keys || []));
    }, [companyId, request]);

    useEffect(() => {
        if (companyId) {
            load();
        }
    }, [companyId, load]);

    const save = () => {
        request(http.post('/signing-key/create', { company_id: companyId, name: form.name, private_key: form.privateKey }), () => {
            enqueueSnackbar('Key has been added', { action: CustomSnackbar, variant: 'success' });
            load();
        });
        setForm(null);
    };

    const remove = () => {
        request(http.delete(`/signing-key/${deleting}?company_id=${companyId}`), () => {
            enqueueSnackbar('Key has been deleted', { action: CustomSnackbar, variant: 'success' });
            load();
        });
        setDeleting(null);
    };

    return (
        <>
            <Box sx={{ display: 'flex', marginBottom: '20px' }}>
                <Button variant="contained" onClick={() => setForm(EMPTY_FORM)} disabled={!companyId} sx={{ marginLeft: 'auto' }}>
                    Add key
                </Button>
            </Box>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Public key</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.map((key) => (
                            <TableRow key={key.name}>
                                <TableCell sx={{ fontFamily: 'monospace' }}>{key.name}</TableCell>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', whiteSpace: 'pre', overflowX: 'auto', maxWidth: '600px' }}>
                                    {key.public_key}
                                </TableCell>
                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    <Button onClick={() => copy(key.public_key, 'Public key')}>Copy public key</Button>
                                    <Button color="error" onClick={() => setDeleting(key.name)}>Delete</Button>
                                </TableCell>
                            </TableRow>
                        ))}
                        {!keys.length && (
                            <TableRow>
                                <TableCell colSpan={3} sx={{ color: '#99a1ad' }}>No signing keys yet</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={form !== null} onClose={() => setForm(null)} fullWidth maxWidth="sm">
                <DialogTitle>Add signing key</DialogTitle>
                {form && (
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: '20px', paddingTop: '10px !important' }}>
                        <TextField
                            label="Name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            error={form.name !== '' && !KEY_NAME.test(form.name)}
                            helperText="a-z 0-9 _ -, up to 64 characters"
                            inputProps={{ maxLength: 64 }}
                        />
                        <TextField
                            label="Private key (PEM)"
                            value={form.privateKey}
                            onChange={(e) => setForm({ ...form, privateKey: e.target.value })}
                            multiline
                            minRows={8}
                            autoComplete="off"
                            inputProps={{ spellCheck: false, style: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                    </DialogContent>
                )}
                <DialogActions>
                    <Button variant="outlined" onClick={() => setForm(null)}>Close</Button>
                    <Button variant="contained" onClick={save} disabled={!form || !KEY_NAME.test(form.name) || !form.privateKey.trim()}>Save</Button>
                </DialogActions>
            </Dialog>

            <AlertDialog
                visible={deleting !== null}
                onClose={() => setDeleting(null)}
                onAgree={remove}
                title="Delete signing key"
                text={`Delete key ${deleting}? Flows that sign with it will fail.`}
            />
        </>
    );
};
